import { I18n } from '../../../Locales/I18nService';

import { IRoom, ObsoleteRoomDimensions, RoomAngle, RoomShape, RoomType } from '../Models/IRoom';

const BaseRoom: IRoom = {
    type: RoomType.Sol,
    name: '',
    roomType: '',
    roomFloor: '',
    roomExistingFlooring: '',
    roomExistingFloorType: '',
    roomHeating: '',
    roomHeatingType: '',
    openings: [],
    roomItems: [],
    walls: [],
};

const toDiamondDimensions = (width: number, height: number, angle: RoomAngle): ObsoleteRoomDimensions => {
    const min = Math.min(width, height);
    const table = min / 1.5;
    const unkownNumber = Math.sqrt(Math.pow(table, 2) / 2);
    const left_crown = (angle === 0 || angle === 180 ? width : height) - unkownNumber;
    const right_crown = (angle === 0 || angle === 180 ? height : width) - unkownNumber;
    //console.log('toDiamondDimension', { width, height, min, table, unkownNumber, left_crown, right_crown })
    return { left_crown, table, right_crown };
};

const FLYING_WING_SIZE_RATIO = 0.5; //0.625;(ratio de base avec les anciens tailles en dur)

export class RoomFactory {
    //#region //* NAME GENERATOR

    public static defaultName = (roomCount: number = 0, type: RoomType = RoomType.Sol) => {

        let baseName = 'Pièce';

        if (type === RoomType.Sol) {
            baseName = 'Pièce';
        }
        if (type === RoomType.Wall) {
            baseName = 'Mur';
        }
        if (type === RoomType.Stairs) {
            baseName = 'Escalier';
        }

        return `${baseName} ${roomCount + 1}`

    }


    public static isDefaultName = (roomName: string = '') => {
        const [name, num] = roomName.split(' ');
        return ['Pièce', 'Mur', 'Escalier'].includes(name) && Boolean(num.length) && Number.isFinite(parseInt(num));
    };

    public static getDuplicateName(room: IRoom, rooms: Array<IRoom>) {
        const roomNumbers = rooms
            .filter((r) => r.name)
            .map((r) => {
                const num = r.name!.split(' ')[1];
                return parseInt(num);
            });
        if (roomNumbers.length) {
            const name = room.name!.split(' ')[0];
            const max = Math.max(...roomNumbers) + 1;
            return `${name} ${max}`;
        }
        return `${room.name} 2`;
    }

    public static toUniqueName = (rooms: Array<IRoom>, currentName: string = '', roomType: string = '', roomFloor: string = '') => {
        if (roomFloor === I18n.get('RoomFloorList_Etage1a3') || roomFloor === I18n.get('RoomFloorList_4etPlus')) {
            roomFloor = I18n.get('RoomFloorList_Floor'); //floor exception, fr string
        }

        let wantedRoomName = roomType || currentName;
        if (!rooms.some((x) => x.name === wantedRoomName)) {
            return wantedRoomName;
        } else {
            let i = 2;
            wantedRoomName = `${roomType} ${i}`;
            // eslint-disable-next-line no-loop-func
            while (rooms.some((x) => x.name === wantedRoomName)) {
                wantedRoomName = `${roomType} ${i}`;
                i++;
            }
            return wantedRoomName;
        }
    };

    //#endregion

    public static createBaseRoom = (
        roomShape: RoomShape,
        width: number,
        height: number,
        type: RoomType = RoomType.Sol,
        angle: RoomAngle
    ): IRoom => {
        switch (roomShape) {
            case RoomShape.Rectangle:
                return this.createRectangle(width, height, type);
            case RoomShape.Diamond:
                return this.createDiamond(width, height, type, angle);

            case RoomShape.Flyingwing:
                return this.createFlyingwing(width, height, angle);

            case RoomShape.U:
                return this.createU(width, height, angle);

            case RoomShape.Plus:
                return this.createPlus(width, height);

            default:
                throw new Error(`the ${roomShape} is not supported`);
        }
    };
    //#region //* RECTANGE

    private static createRectangle = (width: number, height: number, type: RoomType): IRoom => {
        return {
            ...BaseRoom,
            type,
            shape: RoomShape.Rectangle,
            name: '',
            roomType: '',
            roomFloor: '',
            roomExistingFlooring: '',
            roomExistingFloorType: '',
            roomHeating: '',
            roomHeatingType: '',
            obsoleteDimensions: { width, height },
            openings: [],
            roomItems: [],
            angle: 0,
        };
    };

    //#endregion

    //#region //* DIAMOND
    private static createDiamond = (width: number, height: number, type: RoomType, angle: RoomAngle): IRoom => {
        return {
            ...BaseRoom,
            type,
            shape: RoomShape.Diamond,
            name: '',
            roomType: '',
            roomFloor: '',
            roomExistingFlooring: '',
            roomExistingFloorType: '',
            roomHeating: '',
            roomHeatingType: '',
            obsoleteDimensions: toDiamondDimensions(height, width, angle),
            openings: [],
            roomItems: [],
            angle: angle,
        };
    };

    //#endregion

    //#region  //* FLYING
    private static createFlyingwing = (width: number, height: number, angle: RoomAngle): IRoom => {
        let right_trailing_edge;
        let left_wingtip;
        let right_wingtip;
        let left_trailing_edge;
        switch (angle) {
            default:
            case 0:
                right_trailing_edge = width * FLYING_WING_SIZE_RATIO;
                left_wingtip = width - right_trailing_edge;
                right_wingtip = height / 2;
                left_trailing_edge = height / 2;
                break;
            case 90:
                left_trailing_edge = width * FLYING_WING_SIZE_RATIO;
                right_wingtip = width - left_trailing_edge;
                right_trailing_edge = height / 2;
                left_wingtip = height / 2;
                break;
            case 180:
                right_trailing_edge = width * FLYING_WING_SIZE_RATIO;
                left_wingtip = width - right_trailing_edge;
                left_trailing_edge = height / 2;
                right_wingtip = height / 2;
                break;
            case 270:
                left_trailing_edge = width * FLYING_WING_SIZE_RATIO;
                right_wingtip = width - left_trailing_edge;
                right_trailing_edge = height / 2;
                left_wingtip = height / 2;
                break;
        }
        const dimensions = { right_wingtip, right_trailing_edge, left_trailing_edge, left_wingtip };
        return {
            ...BaseRoom,
            __comment_enum__: 'flyingwing_topleft, flyingwing_topright; flyingwing_bottomleft; flyingwing_bottomright',
            shape: RoomShape.Flyingwing,
            name: '',
            roomType: '',
            roomFloor: '',
            roomExistingFlooring: '',
            roomExistingFloorType: '',
            roomHeating: '',
            roomHeatingType: '',
            obsoleteDimensions: dimensions,
            openings: [],
            roomItems: [],
            angle: angle,
            type: RoomType.Sol,
        };
    };

    //#endregion

    //#region //* U
    private static createU = (width: number, height: number, angle: RoomAngle): IRoom => {
        let exterior_base;
        let left_exterior_edge;
        let left_tip;
        let left_interior_edge;
        let right_tip;
        let right_exterior_edge;
        const tipConstant = 0.375;
        console.log({ width, height, angle });
        const roundedThird =
            angle === 0 || angle === 180
                ? Math.ceil(width * tipConstant * 100) / 100
                : Math.ceil(height * tipConstant * 100) / 100; //avoid aving infinite decimals
        switch (angle) {
            default:
            case 0:
            case 180:
                exterior_base = width;
                left_exterior_edge = height;
                left_tip = roundedThird;
                left_interior_edge = height / 2;
                right_tip = roundedThird;
                right_exterior_edge = height;
                break;
            case 90:
            case 270:
                exterior_base = height;
                left_exterior_edge = width;
                left_tip = roundedThird;
                left_interior_edge = height / 2;
                right_tip = roundedThird;
                right_exterior_edge = width;
                break;
        }

        const dimensions = {
            exterior_base,
            left_exterior_edge,
            left_tip,
            left_interior_edge,
            right_tip,
            right_exterior_edge,
        };

        return {
            shape: RoomShape.U,
            name: '',
            roomType: '',
            roomFloor: '',
            roomExistingFlooring: '',
            roomExistingFloorType: '',
            roomHeating: '',
            roomHeatingType: '',
            obsoleteDimensions: dimensions,
            openings: [],
            roomItems: [],
            angle: angle,
            walls: [],
            type: RoomType.Sol,
        };
    };

    private static createPlus = (width: number, height: number): IRoom => {
        const roundedThirdHorizontal = Math.ceil((width / 3) * 100) / 100; //avoid aving infinite decimals
        const edgesHorizontal = (width - roundedThirdHorizontal) / 2;
        const roundedThirdVertical = Math.ceil((height / 3) * 100) / 100; //avoid aving infinite decimals
        const edgesVertical = (height - roundedThirdVertical) / 2;

        console.log({ roundedThirdHorizontal, edgesHorizontal, roundedThirdVertical, edgesVertical });

        return {
            shape: RoomShape.Plus,
            name: '',
            roomType: '',
            roomFloor: '',
            roomExistingFlooring: '',
            roomExistingFloorType: '',
            customPoints: [
                { x: 0, y: 0 },
                { x: edgesHorizontal, y: 0 },
                { x: edgesHorizontal, y: edgesVertical },
                { x: edgesHorizontal + roundedThirdHorizontal, y: edgesVertical },
                { x: edgesHorizontal + roundedThirdHorizontal, y: edgesVertical + roundedThirdVertical },
                { x: edgesHorizontal, y: edgesVertical + roundedThirdVertical },
                { x: edgesHorizontal, y: edgesVertical * 2 + roundedThirdVertical },
                { x: 0, y: edgesVertical * 2 + roundedThirdVertical },
                { x: 0, y: edgesVertical + roundedThirdVertical },
                { x: -edgesHorizontal, y: edgesVertical + roundedThirdVertical },
                { x: -edgesHorizontal, y: edgesVertical },
                { x: 0, y: edgesVertical },
            ],
            openings: [],
            roomItems: [],
            angle: 0,
            walls: [],
            type: RoomType.Sol,
        };
    };
}
